import * as React from 'react'
import { throttle } from 'lodash-es'
import { PaneContainer, Pane, PaneHandle } from '@cloudflare/draggable-pane'
import { FlexPassthrough, SidebarAltTheme, MonacoEditor } from '../'
import { TKeyVals } from '@cloudflare/cf-component-key-val-editor'
import {
  RequestEditor,
  TEWRequest,
} from '@cloudflare/cf-edgeworker-http-request-editor'
import { RequestStatusIndicator } from '@cloudflare/cf-component-request-status-indicator'
import { createComponent } from '@cloudflare/style-container'
import { Button, ButtonGroup } from '@cloudflare/component-button'

import { EditorWrapper } from './common'
import { TPreviewProps, IRawRequestResponse } from '../lib/FiddleService'

export interface IRawRequestPaneProps {
  isActive: boolean
  pathToMonaco: string
  getPreviewId: (props: TPreviewProps) => string
  runRawRequest: (props: TPreviewProps) => Promise<IRawRequestResponse>
  preview: TPreviewProps
  onChange: (req: TEWRequest) => void
  onSubmit: () => void
  disabled?: boolean
  shouldHideEditorOverflow: boolean
}

export interface IRawRequestPaneState {
  previewId?: string
  requestEditorWidth: number
  rawRequestResponse?: {
    headers: TKeyVals
    body: string
    statusText: string
    statusCode: number
  }
  shouldHideEditorOverflow: boolean
}

export class RawRequestPane extends React.Component<
  IRawRequestPaneProps,
  IRawRequestPaneState
> {
  public editor: monaco.editor.ICodeEditor

  getDerivedState(props: IRawRequestPaneProps) {
    return {
      previewId: props.getPreviewId(props.preview),
    }
  }

  constructor(props: IRawRequestPaneProps) {
    super(props)

    this.state = {
      requestEditorWidth: 400,
      shouldHideEditorOverflow: false,
    }

    this.onRequestSidebarPaneMove = this.onRequestSidebarPaneMove.bind(this)
    this.onEditorMount = this.onEditorMount.bind(this)
    this.onRequestSidebarPaneMouseUp = this.onRequestSidebarPaneMouseUp.bind(
      this,
    )
    this.onRequestSidebarPaneMouseDown = this.onRequestSidebarPaneMouseDown.bind(
      this,
    )
  }

  componentWillReceiveProps(props: IRawRequestPaneProps) {
    if (!props.isActive) {
      return
    }

    const newState = this.getDerivedState(props)

    let needsUpdate: boolean

    if (!this.props.isActive) {
      // if wasn't previously active, check that our latest state corresponds to props
      needsUpdate = newState.previewId !== this.state.previewId
    } else {
      // if was already active, don't update until explicitly asked
      needsUpdate = props.preview.updateCount !== this.props.preview.updateCount
    }

    this.setState(newState)

    if (needsUpdate) {
      this.runTest(props)
    }
  }

  componentDidUpdate(props: IRawRequestPaneProps, state: IRawRequestPaneState) {
    if (this.editor) {
      if (state.rawRequestResponse !== this.state.rawRequestResponse) {
        this.editor.setValue(this.getResponseValue())
      }

      this.editorLayout()
    }
  }

  componentDidMount() {
    if (this.props.isActive) {
      this.setState(this.getDerivedState(this.props))
      this.runTest()
    }
  }

  onEditorMount(editor: monaco.editor.ICodeEditor) {
    this.editor = editor
  }

  getResponseValue() {
    return [
      this.state.rawRequestResponse.headers
        .map(pair => pair.join(': '))
        .join('\n'),
      this.state.rawRequestResponse.body,
    ].join('\n\n')
  }

  render() {
    return (
      <PaneContainer
        direction="row"
        shouldResizeFirstPane={false}
        onRequestMove={this.onRequestSidebarPaneMove}
        onMouseDown={this.onRequestSidebarPaneMouseDown}
        onMouseUp={this.onRequestSidebarPaneMouseUp}
      >
        <Pane>
          <FlexPassthrough direction="column">
            {this.state.rawRequestResponse && (
              <StatusIndicatorWrapper>
                <RequestStatusIndicator
                  code={this.state.rawRequestResponse.statusCode}
                  text={this.state.rawRequestResponse.statusText}
                />
              </StatusIndicatorWrapper>
            )}
            {this.state.rawRequestResponse && (
              <EditorWrapper
                hideOverflow={
                  this.props.shouldHideEditorOverflow ||
                  this.state.shouldHideEditorOverflow
                }
              >
                <MonacoEditor
                  value={this.getResponseValue()}
                  pathToMonaco={this.props.pathToMonaco}
                  minimap={{ enabled: false }}
                  tabSize={2}
                  lineNumbers="off"
                  editorDidMount={this.onEditorMount}
                  readOnly={true}
                />
              </EditorWrapper>
            )}
          </FlexPassthrough>
        </Pane>
        <PaneHandle current={this.state.requestEditorWidth} />
        <Pane width={`${this.state.requestEditorWidth}px`}>
          <FlexPassthrough direction="column">
            <Scrollable>
              <ModulePadding>
                <RequestEditor
                  request={this.props.preview.request}
                  onChange={this.props.onChange}
                  onSubmit={this.props.onSubmit}
                />
              </ModulePadding>
            </Scrollable>
            <SidebarAltTheme>
              <ModulePadding>
                <div style={{ flex: 1, textAlign: 'right' }}>
                  <ButtonGroup spaced={true}>
                    <Button
                      type="primary"
                      onClick={this.props.onSubmit}
                      disabled={this.props.disabled}
                    >
                      Run Test
                    </Button>
                  </ButtonGroup>
                </div>
              </ModulePadding>
            </SidebarAltTheme>
          </FlexPassthrough>
        </Pane>
      </PaneContainer>
    )
  }

  private editorLayout = throttle(() => {
    if (this.editor) {
      this.editor.layout()
    }
  }, 100)

  async runTest(props = this.props) {
    const rawRequestResponse = await this.props.runRawRequest(props.preview)

    this.setState({ rawRequestResponse })
  }

  onRequestSidebarPaneMove(requestEditorWidth: number) {
    this.setState({ requestEditorWidth })
  }

  onRequestSidebarPaneMouseUp() {
    if (this.editor) {
      this.editorLayout()
    }

    this.setState({
      shouldHideEditorOverflow: false,
    })
  }

  onRequestSidebarPaneMouseDown() {
    this.setState({
      shouldHideEditorOverflow: true,
    })
  }
}

const ModulePadding = createComponent(() => ({
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '0.8rem',
  boxSizing: 'border-box',
}))

const Scrollable = createComponent(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'scroll',
}))

const StatusIndicatorWrapper = createComponent(
  ({ theme }: { theme?: any }) => ({
    padding: '0 1rem',
    borderBottom: `solid 1px ${theme.colors.gray[6]}`,
  }),
)



// WEBPACK FOOTER //
// ./src/components/RawRequestPane.tsx